import React, { Suspense, useEffect } from 'react';
import { connect, useDispatch } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import feathers from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import io from 'socket.io-client';
import auth from '@feathersjs/authentication-client';
import ResetPassword from 'pages/ResetPassword';
import { notify } from 'reducers/NotifierReducer';
import { setWebsocketConnection } from './reducers/WebsocketReducer';
import { logOut, setUserAuthenticated, setUserData } from './reducers/UserReducer';
import { ClimbingBoxLoader } from 'react-spinners';
import * as serviceWorker from './serviceWorker';
import { notifyPanel } from './reducers/NotifierReducer';
import Cookies from 'js-cookie';
import { registerGlobalUserStampHook } from './userStampHook';

// Layout Blueprints
import { MinimalLayout, CadLayout } from './layout-blueprints';

// Pages
import PageLogin from './pages/PageLogin';
import Page404 from './pages/Page404';
import LogOut from 'pages/LogOut';

import routeDefinitions, { defaultUrl } from './routeDefinitions';
import { getAccessPermission, getAgenciesAccessPermission } from 'reducers/PermissionsReducer';
import { setConfigurationMenuEnabled } from 'reducers/ConfigReducer';
import { showSpinner, hideSpinner } from 'reducers/UiReducer';

let redirectTo = defaultUrl;

const ProtectedRoute = ({
  component: Component,
  isAuthenticated,
  accessPermission,
  isPermissionLoaded,
  Loading,
  checkRootUser,
  configurationMenuEnabled,
  ...rest
}) => {
  const { path } = rest;

  if (!isAuthenticated && path) redirectTo = path;
  if (isAuthenticated && !isPermissionLoaded) return Loading ? Loading() : '';
  if (checkRootUser && !configurationMenuEnabled && isAuthenticated) {
    return <Redirect to={{ pathname: '/settings' }} />;
  }
  // console.log('coming');
  return (
    <Route
      {...rest}
      render={props =>
        isAuthenticated === true && accessPermission === true ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: '/Login', state: { from: props.location } }} />
        )
      }
    />
  );
};

const LoginRoute = ({ component: Component, isAuthenticated, accessPermission, ...rest }) => {
  return (
    <Route
      {...rest}
      render={props =>
        isAuthenticated === true && accessPermission === true ? (
          <Redirect to={{ pathname: redirectTo, state: { from: props.location } }} />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
};

const Routes = props => {
  const {
    isAuthenticated,
    setWebsocketConnection,
    setUserAuthenticated,
    setUserData,
    setLoggedInUserData,
    configLoaded,
    isPermissionLoaded,
    userData,
    websocket,
  } = props;
  const dispatch = useDispatch();
  const accessPermission =
    getAccessPermission('modules', 'Settings') ||
    Boolean(getAgenciesAccessPermission('modules', 'Settings').length);
  React.useEffect(() => {
    serviceWorker.register({
      onSuccess: () => console.log('service worker registered!'),
      onUpdate: () => {
        sessionStorage.setItem('pending_update', 'true');
      },
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const hasUpdate = sessionStorage.getItem('pending_update');
    if (hasUpdate !== 'true') return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      const waitingServiceWorker = reg.waiting;
      if (!waitingServiceWorker) return;
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
      waitingServiceWorker.onstatechange = (event) => {
        if (event.target.state === 'activated') {
          sessionStorage.removeItem('pending_update');
          window.location.reload();
        }
      };
    });
  }, [isAuthenticated]);

  React.useEffect(() => {
    async function establishConnection() {
      const client = feathers();
      client.configure(
        socketio(
          io(window.__RUNTIME_CONFIG__.REACT_APP_API_URL, {
            transports: ['websocket', 'polling'],
            perMessageDeflate: false,
            timeout: 20000,
            requestTimeout: 20000,
          })
        )
      );
      client.configure(auth({ storageKey: 'auth' }));
      registerGlobalUserStampHook(client);
      try {
        const auth = Cookies.get('auth');
        if (auth) {
          window.localStorage.setItem('auth', auth);
          Cookies.remove('auth');
        }
        const user = await client.reAuthenticate();
        setWebsocketConnection(client);
        setUserData(user);
        setUserAuthenticated(true);
      } catch (error) {
        if (error.code === 401) {
          setUserAuthenticated(false);
          setUserData(null);
          dispatch(setConfigurationMenuEnabled(false));

          setWebsocketConnection(client);
        }
        setWebsocketConnection(client);
      }
    }
    establishConnection();
    // eslint-disable-next-line
  }, [setWebsocketConnection, setUserAuthenticated, setLoggedInUserData]);

  const refreshToken = async () => {
    try {
      props.showSpinner();
      const currentToken = localStorage.getItem('auth');
      const response = await websocket.service('authentication').create({
        strategy: 'jwt',
        accessToken: currentToken,
        isRenewToken: true,
        AppName: 'SETTINGS', 
      });
      localStorage.setItem('auth', response.accessToken);
      websocket.set('authentication', response);
      setUserData(response);
      setUserAuthenticated(true);
      props.notify('Session extended!', 'success');
    } catch (error) {
      console.error('Refresh Failed', error);
      setUserAuthenticated(false);
    } finally {
      props.hideSpinner();
    }
  };

  const showWarning = (expiryTime) => {
    const expiryTimeText = new Date(expiryTime)
      .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  
    props.notifyPanel(
      {
        title: 'Session Expiring Soon!',
        message: `Your session will expire at ${expiryTimeText}. Click CONTINUE to stay signed in.`,
        position: 'tc',
        level: 'warning',
        autoDismiss: props.sessionWarningMinutes * 60,
        action: {
          label: 'CONTINUE',
          callback: refreshToken,
        },
      },
      'warning'
    );
  };

  React.useEffect(() => {    
    if (!isAuthenticated || !userData || !props.sessionWarningEnabled) return;
    const payload = userData?.authentication?.payload || userData?.payload;  
    const exp = payload?.exp;
    if (!exp) return;  
    const expiryTime = exp * 1000;
    const warningSeconds = Number(props.sessionWarningMinutes) * 60;
    const now = Date.now();
    const delay = expiryTime - now - (warningSeconds * 1000);

    if (delay <= 0) {
      if (now < expiryTime) {
      showWarning(expiryTime);
      } 
      return;
    }
    const timer = setTimeout(() => {
      showWarning(expiryTime);
    }, delay);
    return () => clearTimeout(timer);
}, [isAuthenticated, userData, props.sessionWarningEnabled]);

  const SuspenseLoading = () => {
    return (
      <div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
        <div className="d-flex align-items-center flex-column px-4">
          <ClimbingBoxLoader color={'#0153a3'} loading={true} />
        </div>
        <div className="text-muted font-size-xl text-center pt-3">
          Please wait while we load the view...
        </div>
      </div>
    );
  };

  const loadingPage = props => <h3>Loading...</h3>;

  return (
    <Suspense fallback={<SuspenseLoading />}>
      <Switch>
        <Redirect exact from="/" to="/Login" />
        <Route path={['/Login']}>
          <MinimalLayout>
            <LoginRoute
              path="/Login"
              component={PageLogin}
              isAuthenticated={isAuthenticated}
              accessPermission={accessPermission}
            />
          </MinimalLayout>
        </Route>
        <Route path={['/logout']}>
          {' '}
          <LogOut />
        </Route>
        <Route path={['/Reset-password']}>
          <MinimalLayout>
            <ResetPassword isAuthenticated={isAuthenticated} />
          </MinimalLayout>
        </Route>
        {routeDefinitions.map((route, key) => {
          const Layout = route.layout;
          return (
            <Route path={[route.url]} key={key}>
              <Layout>
                <ProtectedRoute
                  path={route.url}
                  component={configLoaded ? route.component : loadingPage}
                  isAuthenticated={isAuthenticated}
                  accessPermission={accessPermission}
                  isPermissionLoaded={isPermissionLoaded}
                  Loading={loadingPage}
                  checkRootUser={route.ptsUser}
                  configurationMenuEnabled={props.configurationMenuEnabled}
                />
              </Layout>
            </Route>
          );
        })}
        <Route path={['/Not-Found']}>
          <MinimalLayout>
            <Page404 />
          </MinimalLayout>
        </Route>
        <Route>
          <CadLayout>
            <ProtectedRoute component={Page404} isAuthenticated={isAuthenticated} />
          </CadLayout>
        </Route>
      </Switch>
    </Suspense>
  );
};

const mapStateToProps = state => ({
  network: state.offline,
  isAuthenticated: state.user.isAuthenticated,
  configLoaded: state.config.loaded,
  configurationMenuEnabled: state.config.configurationMenuEnabled,
  isPermissionLoaded: state.permissions.loaded,
  userData: state.user.userData,
  websocket: state.websocket.websocket,
  sessionWarningEnabled: state.config.sessionWarningEnabled,
  sessionWarningMinutes: state.config.sessionWarningMinutes,
});

export default connect(mapStateToProps, {
  setWebsocketConnection,
  setUserAuthenticated,
  setUserData,
  notifyPanel,
  notify,
  logOut,
  showSpinner, 
  hideSpinner,
})(Routes);
